import * as THREE from 'three';

/**
 * ===========================================================================
 *  WEAPON TRAILS — the swept ribbon
 * ===========================================================================
 *
 * This is the single most recognisable Dynasty Warriors visual, and it is
 * *not* a particle effect: it is a strip of quads generated from the real
 * swept path of the blade. Every frame each live trail samples two points —
 * the blade TIP and the blade ROOT (near the hilt) — and pushes them into a
 * rolling buffer. The strip between consecutive samples is the surface the
 * blade actually passed through.
 *
 * Four things make it read as DW rather than as a smear:
 *
 *  1. SUB-FRAME INTERPOLATION. At 60 Hz a fast sweep turns 140 degrees in
 *     three frames, so raw samples give a visibly faceted fan. Each frame
 *     pushes `SUB` Catmull-Rom samples through the last raw transforms, which
 *     curves the ribbon along the true arc.
 *  2. TAPER. The newest sample is full width (tip to root); older samples are
 *     drawn in toward the tip edge, so the ribbon comes to a point behind the
 *     blade instead of ending in a blunt rectangle.
 *  3. A HOT LEADING EDGE. Colour and alpha both ramp: the newest samples are
 *     near white and the tail falls to the weapon's element colour and then to
 *     nothing over `fadeFrames` (~10) frames.
 *  4. DEAD-SAMPLE REJECTION. A blade that has not moved 5 mm does not push,
 *     so hitstop and idle never flush the arc out of the buffer.
 *
 * Every ribbon in the game lives in ONE geometry and costs ONE additive draw
 * call. Unused slots collapse to a degenerate point with zero alpha.
 */

const SUB = 3;                 // interpolated samples pushed per rendered frame
const MIN_STEP2 = 0.005 * 0.005;

const _p0 = new THREE.Vector3(), _p1 = new THREE.Vector3();

/** Catmull-Rom between b and c, tangents from a and d. */
function cr(a, b, c, d, u) {
  const u2 = u * u, u3 = u2 * u;
  return 0.5 * ((2 * b) + (-a + c) * u + (2 * a - 5 * b + 4 * c - d) * u2 + (-a + 3 * b - 3 * c + d) * u3);
}

export class TrailSet {
  /**
   * @param {number} count   concurrent ribbons
   * @param {number} samples ribbon length in samples (SUB pushed per frame)
   * @param {THREE.Texture} map ribbon gradient
   */
  constructor(count = 10, samples = 36, map = null) {
    this.count = count;
    this.S = samples;
    const V = count * samples * 2;

    this.pos = new Float32Array(V * 3);
    this.uvs = new Float32Array(V * 2);
    this.col = new Float32Array(V * 4);

    const idx = new Uint16Array(count * (samples - 1) * 6);
    let o = 0;
    for (let t = 0; t < count; t++) {
      const base = t * samples * 2;
      for (let j = 0; j < samples - 1; j++) {
        const a = base + j * 2, b = a + 1, c = a + 2, d = a + 3;
        idx[o++] = a; idx[o++] = b; idx[o++] = d;
        idx[o++] = a; idx[o++] = d; idx[o++] = c;
      }
      for (let j = 0; j < samples; j++) {
        const k = (base + j * 2) * 2;
        const u = j / (samples - 1);
        this.uvs[k] = u; this.uvs[k + 1] = 0;        // root edge
        this.uvs[k + 2] = u; this.uvs[k + 3] = 1;    // blade edge
      }
    }

    const g = new THREE.BufferGeometry();
    this.aPos = new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.BufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this.aPos);
    g.setAttribute('uv', new THREE.BufferAttribute(this.uvs, 2));
    g.setAttribute('color', this.aCol);
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.geometry = g;

    this.material = new THREE.MeshBasicMaterial({
      map, vertexColors: true, transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false, depthTest: true,
      side: THREE.DoubleSide, toneMapped: false, fog: false,
    });
    this.material.name = 'vfxTrail';

    this.mesh = new THREE.Mesh(g, this.material);
    this.mesh.name = 'vfxTrails';
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = 12;
    this.mesh.castShadow = this.mesh.receiveShadow = false;

    /** @type {Trail[]} */
    this.slots = [];
    for (let i = 0; i < count; i++) this.slots.push(new Trail(this, i));
    this._dirty = true;
    this.active = 0;
  }

  /** Grab a free ribbon. Steals the oldest fading one if the pool is full. */
  acquire(opts, now = 0) {
    for (let i = 0; i < this.slots.length; i++) {
      const t = this.slots[i];
      if (!t.busy) return t.begin(opts, now);
    }
    let best = null;
    for (let i = 0; i < this.slots.length; i++) {
      const t = this.slots[i];
      if (!t.on && (!best || t.startedAt < best.startedAt)) best = t;
    }
    return best ? best.begin(opts, now) : null;
  }

  update(now, dt) {
    let any = 0;
    for (let i = 0; i < this.slots.length; i++) {
      const t = this.slots[i];
      if (t.busy) { t.step(now, dt); any++; }
    }
    this.active = any;
    this.mesh.visible = any > 0;
    if (any || this._dirty) {
      this.aPos.needsUpdate = true;
      this.aCol.needsUpdate = true;
      this._dirty = any > 0;
    }
  }

  clear() {
    for (const t of this.slots) t.kill();
    this.pos.fill(0); this.col.fill(0);
    this.aPos.needsUpdate = this.aCol.needsUpdate = true;
  }

  dispose() { this.geometry.dispose(); this.material.dispose(); }
}

/**
 * One ribbon. This object is the handle `world.vfx.trail()` hands back:
 *
 *   h.push(tx,ty,tz, rx,ry,rz)   feed one blade transform (tip, then root)
 *   h.follow(obj3d, tipY, rootY) sample an Object3D's local +Y axis instead
 *   h.configure(opts)            retune colour / width / intensity mid-swing
 *   h.stop()                     stop feeding; the tail fades out and frees
 *   h.kill()                     drop it instantly
 *   h.alive                      still on screen?
 */
export class Trail {
  constructor(set, slot) {
    this.set = set; this.slot = slot;
    this.S = set.S;
    this.busy = false; this.on = false;
    this.startedAt = 0;
    this.buf = new Float32Array(this.S * 6);     // rolling tip/root samples
    this.n = 0; this.head = 0;
    this.raw = new Float32Array(3 * 6);          // last three raw transforms
    this.rawN = 0;
    this.age = 0;                                // sub-samples since stop()
    this.obj = null; this.tipY = 1.8; this.rootY = 0.15;
    this.width = 1; this.intensity = 1; this.fadeFrames = 10;
    this.r0 = 1.0; this.g0 = 1.0; this.b0 = 0.96;
    this.r1 = 0.85; this.g1 = 0.55; this.b1 = 0.22;
    this.taper = 0.55;
    this.outset = 0;      // push the blade edge this fraction past the tip
    this._fed = false; this._starve = 0;
  }

  get alive() { return this.busy; }

  begin(o, now = 0) {
    this.busy = true; this.on = true;
    this.n = 0; this.head = 0; this.rawN = 0; this.age = 0;
    this._fed = false; this._starve = 0;
    this.startedAt = now;
    this.buf.fill(0);
    this.configure(o || {});
    return this;
  }

  configure(o = {}) {
    if (o.width !== undefined) this.width = o.width;
    if (o.intensity !== undefined) this.intensity = o.intensity;
    if (o.taper !== undefined) this.taper = o.taper;
    if (o.outset !== undefined) this.outset = o.outset;
    if (o.fadeFrames !== undefined) this.fadeFrames = o.fadeFrames;
    if (o.hot) { this.r0 = o.hot[0]; this.g0 = o.hot[1]; this.b0 = o.hot[2]; }
    if (o.tail) { this.r1 = o.tail[0]; this.g1 = o.tail[1]; this.b1 = o.tail[2]; }
    if (o.obj !== undefined) this.obj = o.obj;
    if (o.tipY !== undefined) this.tipY = o.tipY;
    if (o.rootY !== undefined) this.rootY = o.rootY;
    return this;
  }

  /** Track an Object3D: the blade is its local +Y axis from rootY to tipY. */
  follow(obj, tipY = 1.8, rootY = 0.15) {
    this.obj = obj; this.tipY = tipY; this.rootY = rootY;
    return this;
  }

  stop() { this.on = false; return this; }

  kill() {
    this.busy = false; this.on = false; this.n = 0; this.rawN = 0; this.obj = null;
    const S = this.S, base = this.slot * S * 2;
    const pos = this.set.pos, col = this.set.col;
    for (let j = 0; j < S * 2; j++) {
      const k3 = (base + j) * 3, k4 = (base + j) * 4;
      pos[k3] = pos[k3 + 1] = pos[k3 + 2] = 0;
      col[k4] = col[k4 + 1] = col[k4 + 2] = col[k4 + 3] = 0;
    }
    this.set._dirty = true;
    return this;
  }

  /** Feed one raw blade transform: tip (tx,ty,tz) then root (rx,ry,rz). */
  push(tx, ty, tz, rx, ry, rz) {
    const r = this.raw;
    if (this.rawN > 0) {
      const dx = tx - r[12], dy = ty - r[13], dz = tz - r[14];
      const dr = rx - r[15], dg = ry - r[16], db = rz - r[17];
      if (dx * dx + dy * dy + dz * dz < MIN_STEP2 &&
          dr * dr + dg * dg + db * db < MIN_STEP2) { this._fed = true; return this; }
    }
    r.copyWithin(0, 6);
    r[12] = tx; r[13] = ty; r[14] = tz; r[15] = rx; r[16] = ry; r[17] = rz;
    if (this.rawN < 3) this.rawN++;
    this._fed = true;

    if (this.rawN < 2) { this._store(tx, ty, tz, rx, ry, rz); return this; }
    // Interpolate the segment previous->current. The fourth control point is
    // the reflection of the current sample, so the ribbon head is never a
    // frame behind the blade.
    const aI = this.rawN >= 3 ? 0 : 6, bI = 6, cI = 12;
    for (let s = 1; s <= SUB; s++) {
      const u = s / SUB;
      this._store(
        cr(r[aI], r[bI], r[cI], 2 * r[cI] - r[bI], u),
        cr(r[aI + 1], r[bI + 1], r[cI + 1], 2 * r[cI + 1] - r[bI + 1], u),
        cr(r[aI + 2], r[bI + 2], r[cI + 2], 2 * r[cI + 2] - r[bI + 2], u),
        cr(r[aI + 3], r[bI + 3], r[cI + 3], 2 * r[cI + 3] - r[bI + 3], u),
        cr(r[aI + 4], r[bI + 4], r[cI + 4], 2 * r[cI + 4] - r[bI + 4], u),
        cr(r[aI + 5], r[bI + 5], r[cI + 5], 2 * r[cI + 5] - r[bI + 5], u));
    }
    return this;
  }

  _store(tx, ty, tz, rx, ry, rz) {
    const S = this.S;
    this.head = (this.head + 1) % S;
    const k = this.head * 6, b = this.buf;
    b[k] = tx; b[k + 1] = ty; b[k + 2] = tz;
    b[k + 3] = rx; b[k + 4] = ry; b[k + 5] = rz;
    if (this.n < S) this.n++;
  }

  /** Driven once per rendered frame by TrailSet.update(). */
  step(now, dt) {
    const frames = Math.max(1, Math.round(dt * 60));
    if (this.on && this.obj) {
      const m = this.obj.matrixWorld.elements;
      const ty = this.tipY, ry = this.rootY;
      _p0.set(m[4] * ty + m[12], m[5] * ty + m[13], m[6] * ty + m[14]);
      _p1.set(m[4] * ry + m[12], m[5] * ry + m[13], m[6] * ry + m[14]);
      this.push(_p0.x, _p0.y, _p0.z, _p1.x, _p1.y, _p1.z);
    }
    if (this.on) {
      // a hand-driven trail nobody fed for a few frames has been abandoned
      if (this._fed) { this._starve = 0; this._fed = false; }
      else if (!this.obj && ++this._starve > 3) this.on = false;
      this.age = 0;
    } else {
      this.age += frames * SUB;
      if (this.n <= 1 || this.age > this.fadeFrames * SUB) { this.kill(); return; }
    }
    this._write();
  }

  _write() {
    const S = this.S, N = this.n;
    const base = this.slot * S * 2;
    const pos = this.set.pos, col = this.set.col, b = this.buf;
    const fade = Math.max(2, this.fadeFrames * SUB);
    const w = this.width, inten = this.intensity, tp = this.taper, os = this.outset;
    const oldest = N > 0 ? ((this.head - (N - 1) + S * 4) % S) * 6 : 0;

    for (let j = 0; j < S; j++) {
      const ageIdx = S - 1 - j;                  // 0 at the leading edge
      const k3 = (base + j * 2) * 3, k4 = (base + j * 2) * 4;
      if (ageIdx >= N) {
        const ox = N > 0 ? b[oldest] : 0, oy = N > 0 ? b[oldest + 1] : 0, oz = N > 0 ? b[oldest + 2] : 0;
        pos[k3] = ox; pos[k3 + 1] = oy; pos[k3 + 2] = oz;
        pos[k3 + 3] = ox; pos[k3 + 4] = oy; pos[k3 + 5] = oz;
        col[k4] = col[k4 + 1] = col[k4 + 2] = col[k4 + 3] = 0;
        col[k4 + 4] = col[k4 + 5] = col[k4 + 6] = col[k4 + 7] = 0;
        continue;
      }
      const src = ((this.head - ageIdx + S * 4) % S) * 6;
      const tx = b[src], ty = b[src + 1], tz = b[src + 2];
      const rx = b[src + 3], ry = b[src + 4], rz = b[src + 5];

      const a01 = (ageIdx + this.age) / fade;
      const life = a01 >= 1 ? 0 : 1 - a01;
      const narrow = 1 - tp * (1 - life * life);
      const cx = tx + (rx - tx) * narrow * w;
      const cy = ty + (ry - ty) * narrow * w;
      const cz = tz + (rz - tz) * narrow * w;
      // the outer glow ribbon reaches a little past the point of the blade
      const bx = os ? tx + (tx - rx) * os : tx;
      const by = os ? ty + (ty - ry) * os : ty;
      const bz = os ? tz + (tz - rz) * os : tz;

      pos[k3] = cx; pos[k3 + 1] = cy; pos[k3 + 2] = cz;         // root edge
      pos[k3 + 3] = bx; pos[k3 + 4] = by; pos[k3 + 5] = bz;     // blade edge

      const hot = life * life * life;
      const rr = this.r1 + (this.r0 - this.r1) * hot;
      const gg = this.g1 + (this.g0 - this.g1) * hot;
      const bb = this.b1 + (this.b0 - this.b1) * hot;
      const al = Math.pow(life, 1.5) * inten;
      // The leading edge sits far above 1.0 so it blows to white and the bloom
      // pyramid finds it; the body deliberately sits BELOW 1.0 so the tonemap
      // does not wash its colour out. A ribbon that is uniformly over-bright
      // is a white smear — the DW read is a white-hot edge dragging a coloured
      // body behind it.
      const gain = 0.52 + 4.40 * hot;
      col[k4] = rr * gain; col[k4 + 1] = gg * gain; col[k4 + 2] = bb * gain;
      col[k4 + 3] = al * 0.82;                                  // root edge dimmer
      col[k4 + 4] = rr * gain; col[k4 + 5] = gg * gain; col[k4 + 6] = bb * gain;
      col[k4 + 7] = al;
    }
    this.set._dirty = true;
  }
}
